<script lang="ts" context="module">
  import type { schema } from "@editor/schema";
  import { storeExportedContent } from "@editor/app/canva";
  import Tooltip from "@ui/Tooltip.svelte";
  import Switch from "@ui/Switch.svelte";
  import Spinner from "@ui/Spinner.svelte";
  import Hint from "@ui/Hint.svelte";
  import FieldLabel from "@ui/form/fields/FieldLabel.svelte";
  import { field } from "../form/store";
  import PhotoField from "../form/PhotoField.svelte";
  import { preload } from "sbelt/image";
  import ErrorModal from "@ui/ErrorModal.svelte";
  import { getCurrentUser, getNewAppUrl } from "@ui/currentUser";
  import { getJson, ServerError } from "@ui/http";

  const codeToMessage: { [key: string]: string } = {
    design_not_found: "Your design was not found in Canva"
  };
</script>

<script lang="ts">
  const { id, setWait } = field();

  export let value: schema.HeaderBlock["custom"] = undefined;

  const currentUser = getCurrentUser();
  const newAppUrl = getNewAppUrl();

  let isProcessing: boolean = false;
  let errorMsg: string | undefined;

  function removeHeader() {
    photoValue = value = undefined;
  }

  async function onDesignPublish(o: {
    // The ID of the user's design.
    designId: string;
    // The title of the user's design.
    designTitle: string;
    // The URL for downloading the user's design. This URL expires after 15 minutes. The URL is in the form https://export-download.canva.com/<export_file>
    exportUrl: string;
  }) {
    if (o.exportUrl) {
      isProcessing = true;

      // const ocr = detectText(o.exportUrl);
      const { url, h } = await storeExportedContent(o.exportUrl);

      value = {
        showText: value?.showText ?? false,
        externalId: o.designId,
        url,
        h: h / 2 // save it in "logical pixels" instead of retina
      };

      // await ocr;
      isProcessing = false;
    }
  }

  $: setWait(isProcessing && "Processing background");

  let showText: boolean = value?.showText ?? false;

  $: if (value) {
    value.showText = showText;
  }

  async function checkJobStatus(statusCheckUrl: string) {
    isProcessing = true;
    const result = await getJson<{ status: "in_progress" | "success"; urls: string[]; designId: string }>(statusCheckUrl);
    if (result.status === "success" && result.urls.length) {
      return onDesignPublish({
        designId: result.designId,
        exportUrl: result.urls[0],
        designTitle: "Smore Newsletter Header"
      });
    }
    setTimeout(() => {
      checkJobStatus(statusCheckUrl);
    }, 150);
  }

  function messageHandler(e: MessageEvent) {
    if (!e.data.isFromSmore) {
      return;
    }

    window.removeEventListener("message", messageHandler);

    if (typeof e.data.code === "string") {
      if (e.data.code === "access_denied") {
        return;
      }
      errorMsg = codeToMessage[e.data] || ServerError.GENERIC_ERROR;
    } else if (e.data.statusCheckUrl) {
      checkJobStatus(new URL(e.data.statusCheckUrl, newAppUrl).toString());
    }
  }

  function openCanvaPopup(existingDesignId?: string) {
    window.addEventListener("message", messageHandler);

    const url = existingDesignId
      ? new URL(`/integrations/canva/${$currentUser.id}/design/${existingDesignId}`, newAppUrl).toString()
      : new URL(`/integrations/canva/${$currentUser.id}/design/create`, newAppUrl).toString();

    const { width: screenWidth, height: screenHeight } = window.screen;

    // Open the popup in the (kinda) center of the screen, and make it about 80% of the screen
    window.open(
      url,
      "canvaPopup",
      `width=${Math.ceil(screenWidth * 0.85)},height=${Math.ceil(screenHeight * 0.8)},top=${Math.ceil(screenHeight * 0.1)},left=${Math.ceil(screenWidth * 0.075)}`
    );
  }

  async function editOrCreateCanvaDesign() {
    const id = value?.externalId;
    openCanvaPopup(id);
  }

  let isUploading = false;

  function uploadStart(e: any) {
    isProcessing = true;
    isUploading = true;
  }

  function uploadDone(e: any) {
    isUploading = false;
    isProcessing = false;

    const url = e.detail.legacy_thumb_url;
    const h = e.detail.thumb_h || 700 / 2;

    // preload the image
    preload(url);

    value = {
      showText: value?.showText ?? false,
      photo: e.detail,
      externalId: "",
      url,
      h
    };
  }

  function uploadEnd(e: any) {
    setTimeout(() => {
      isUploading = false;
      isProcessing = false;
    }, 100);

    errorMsg = e?.detail?.errorMessage;
  }

  let photoValue: any = value?.photo;
  let uploadedPhoto: any | undefined;

  const ASPECT = {
    min: { w: 700, h: 280 },
    max: { w: 700, h: 350 },
    default: { w: 700, h: 300 }
  };
</script>

<ErrorModal bind:errorMsg title="" />

<div class="mb-1 flex items-center">
  <FieldLabel name="Custom header" full={false} />
  <hr class="ml-2 flex-grow" />
</div>
<div class="pb-1 pl-px">
  {#if value || isProcessing}
    <div class="relative flex">
      {#if isProcessing}
        {#if isUploading}
          <div class="mr-3 flex-shrink-0 self-start rounded bg-gray-500" style="width:126px; height:70px">
            <PhotoField
              bind:value={photoValue}
              handoffId="custom-bg-upload"
              on:upload_done={uploadDone}
              on:upload_end={uploadEnd}
              on:logged_out
              ar={ASPECT}
              mode="resize"
              showErrorMessage={false}>
              <svelte:fragment slot="preview">
                <div style="width:126px; height:70px" />
              </svelte:fragment>

              <!-- <img src={previewUrl} height="70" alt="Custom background preview" class="bg-gray-500 rounded " slot="preview" let:previewUrl /> -->
            </PhotoField>
          </div>
        {:else}
          <div class="mr-3 flex flex-shrink-0 items-center justify-center rounded bg-gray-500" style="width:126px; height:70px">
            <Spinner />
          </div>
        {/if}
      {:else if value}
        {#if value.externalId}
          <div class="mr-3 flex-shrink-0 self-start">
            <img src={value.url} width="126" height="70" alt="Custom background preview" class="rounded bg-gray-500" />
          </div>
        {:else}
          <div class="mr-3 flex flex-shrink-0 justify-center self-start rounded bg-gray-500" style="width:126px; height:70px">
            <PhotoField
              bind:value={photoValue}
              handoffId="custom-bg-upload"
              on:upload_done={uploadDone}
              on:upload_end={uploadEnd}
              on:logged_out
              disabled
              bind:this={uploadedPhoto}
              ar={ASPECT}
              mode="resize"
              showErrorMessage={false}>
              <img
                src={previewUrl}
                style="max-height:70px; max-width:126px;"
                alt="Custom background preview"
                class="h-full rounded bg-gray-500"
                slot="preview"
                let:previewUrl />
            </PhotoField>
          </div>
        {/if}
      {/if}

      <i class="material-icons md-18 clickable absolute right-1 top-1 hover:text-red-700" on:click={removeHeader}
        >close

        <Tooltip>Remove custom header</Tooltip>
      </i>

      <div class="flex flex-col items-start justify-between text-14">
        {#if isProcessing}
          Processing...
        {:else if value && value.externalId}
          <!-- We already have custom background here -->
          <!-- We have an external id, edit with canva -->
          <span on:click={editOrCreateCanvaDesign} class="clickable canva-button shadow"><span />Edit header with Canva</span>
        {:else if value}
          <span on:click={() => uploadedPhoto.edit()} class="clickable rounded bg-blue-600 p-2 text-white shadow"
            ><span />Edit custom header</span>
        {/if}

        <div class="mt-3 flex items-center">
          <Switch id="showtext" size="small" align="start" bind:checked={showText}>
            <div class="-mt-0.5">
              <h2 class="flex items-center text-14 font-medium">
                Display header text &nbsp;<Hint>Turn this off if your custom header already includes text</Hint>
              </h2>
            </div>
          </Switch>
        </div>
      </div>
    </div>
  {:else}
    <!-- Empty state -->
    <div class="flex">
      <span on:click={editOrCreateCanvaDesign} class="clickable canva-button mr-1 w-1/2 shadow"><span />Create header with Canva</span>

      <div class="relative ml-1 w-1/2">
        <PhotoField
          ar={ASPECT}
          value={undefined}
          handoffId="custom-bg-upload"
          on:upload_start={uploadStart}
          on:upload_done={uploadDone}
          on:logged_out
          on:upload_end={uploadEnd}
          mode="resize"
          showErrorMessage={false}>
          <div
            slot="empty"
            class="clickable flex h-10 items-center justify-center rounded border-2 border-dashed border-gray-300 bg-white text-14 font-medium shadow hover:border-teal-500">
            + Upload your own
          </div>
        </PhotoField>
      </div>
    </div>
  {/if}
</div>

<style>
  .canva-button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    background: #7d2ae8;
    border-radius: 4px;
    padding-right: 12px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
  }

  .canva-button:hover {
    background-color: #8d39fa;
  }

  .canva-button:active {
    background-color: #6718cf;
  }

  .canva-button span {
    position: relative;
    background-size: cover;
    border-radius: 50%;
    box-sizing: border-box;
    display: block;
    fill: #fff;
    height: 24px;
    width: 24px;
    margin: 6px 7px 7px 10px;
  }

  .canva-button span:after {
    content: " ";
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: transparent;
    background-size: contain;
    background-image: url("data:image/svg+xml;base64,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");
  }

  .canva-button span:before {
    background-color: #00c4cc;
    content: " ";
    display: block;
    position: absolute;
    border-radius: 50%;
    overflow: hidden;
    width: 83.33333%;
    height: 83.33333%;
    left: 50%;
    top: 50%;
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    transition: background-color 0.1s linear;
  }
</style>
